import { useState, type FormEvent } from 'react'

import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart'
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Input,
  NumberStepper,
  PageSpinner,
  Sheet,
  useToast,
} from '@/components/ui'
import { formatDate, formatDayTitle, formatNumber } from '@/lib/format'
import { todayIso } from '@/lib/periods'
import { useInBodySeries, useInBodySummary } from '@/pages/inbody/api'
import { kcalNormSteps } from '@/pages/inbody/targets/logic'

import { useDeleteNutrition, useNutrition, useSaveNutrition, type NutritionPoint } from './api'
import { caloriesSeries, fieldError, nutritionEntries, otherError, proteinSeries } from './logic'
import { DeleteButton, TabToolbar } from './parts'

const FIELDS = ['kcal', 'protein_g', 'fat_g', 'carbs_g', 'water_ml']

function NutritionForm({ entries, onDone }: { entries: NutritionPoint[]; onDone: () => void }) {
  const toast = useToast()
  const save = useSaveNutrition()
  const [day, setDay] = useState(todayIso())
  const existing = entries.find((e) => e.day === day)
  const [kcal, setKcal] = useState<number | null>(existing?.kcal ?? null)
  const [protein, setProtein] = useState<number | null>(existing?.protein_g ?? null)
  const [fat, setFat] = useState<number | null>(existing?.fat_g ?? null)
  const [carbs, setCarbs] = useState<number | null>(existing?.carbs_g ?? null)
  const [water, setWater] = useState<number | null>(existing?.water_ml ?? null)
  const [note, setNote] = useState(existing?.note ?? '')

  // Выбрали день, за который уже есть запись, — подставить её значения для правки.
  const changeDay = (next: string) => {
    setDay(next)
    const found = entries.find((e) => e.day === next)
    setKcal(found?.kcal ?? null)
    setProtein(found?.protein_g ?? null)
    setFat(found?.fat_g ?? null)
    setCarbs(found?.carbs_g ?? null)
    setWater(found?.water_ml ?? null)
    setNote(found?.note ?? '')
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate(
      {
        day,
        data: {
          kcal,
          protein_g: protein,
          fat_g: fat,
          carbs_g: carbs,
          water_ml: water,
          note: note.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.show({ message: 'Питание записано', tone: 'accent' })
          onDone()
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <Input
        label="День"
        type="date"
        value={day}
        max={todayIso()}
        onChange={(e) => changeDay(e.target.value)}
        hint={existing?.id ? 'За этот день уже есть запись — она будет исправлена' : undefined}
      />
      <NumberStepper label="Калории" unit="ккал" value={kcal} onChange={setKcal} step={50} min={0} max={20000} error={fieldError(save.error, 'kcal')} />
      <div className="grid grid-cols-3 gap-3">
        <NumberStepper label="Белки, г" value={protein} onChange={setProtein} min={0} max={1000} error={fieldError(save.error, 'protein_g')} />
        <NumberStepper label="Жиры, г" value={fat} onChange={setFat} min={0} max={1000} error={fieldError(save.error, 'fat_g')} />
        <NumberStepper label="Углеводы, г" value={carbs} onChange={setCarbs} min={0} max={2000} error={fieldError(save.error, 'carbs_g')} />
      </div>
      <NumberStepper label="Вода" unit="мл" value={water} onChange={setWater} step={250} min={0} max={20000} error={fieldError(save.error, 'water_ml')} />
      <Input label="Заметка" value={note} onChange={(e) => setNote(e.target.value)} />
      <ErrorText error={otherError(save.error, ...FIELDS)} />
      <Button type="submit" variant="primary" loading={save.isPending}>
        Сохранить
      </Button>
    </form>
  )
}

const n0 = (v: number | null) => (v === null ? '—' : formatNumber(v, { digits: 0 }))

export function NutritionPage() {
  const query = useNutrition()
  // Норма потребления InBody ступенькой по полным замерам; у последней — норма с дефицитом при цели на снижение.
  const targets = useInBodySummary().data?.nutrition ?? null
  const scans = useInBodySeries('daily_kcal', {})
  const norms = kcalNormSteps(scans.data, targets?.target_kcal ?? null)
  const remove = useDeleteNutrition()
  const toast = useToast()
  const [open, setOpen] = useState(false)

  if (query.isPending) return <PageSpinner />
  if (query.isError) return <ErrorText error={query.error} />

  const points = query.data
  const entries = nutritionEntries(points)
  const addButton = (
    <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
      Записать день
    </Button>
  )

  return (
    <div>
      <TabToolbar
        description="Итог дня вручную: калории, белки, жиры, углеводы и вода. Расход берётся из Garmin, когда он есть."
        action={entries.length ? addButton : null}
      />
      {entries.length === 0 ? (
        <Card>
          <EmptyState
            icon="chart"
            title="Питание ещё не записано"
            description="Внеси итог первого дня — появятся графики калорий и белка."
            action={addButton}
          />
        </Card>
      ) : (
        <div className="grid gap-4">
          <Card
            title="Калории: съедено и расход"
            subtitle={
              norms.length
                ? `Пунктир — ${targets?.deficit ? 'норма InBody для снижения жира' : 'норма InBody'}; дни без данных пропускаются`
                : 'Дни без данных пропускаются'
            }
          >
            <TimeSeriesChart
              series={caloriesSeries(points)}
              defaultPeriod="month"
              goal={norms.length ? { steps: norms, label: 'норма InBody' } : null}
            />
          </Card>
          <Card title="Белок">
            <TimeSeriesChart series={proteinSeries(points)} defaultPeriod="month" showPeriods={false} height={160} />
          </Card>
          <Card title="Записи" flush>
            <ul className="divide-y divide-border">
              {entries.map((p) => (
                <li key={p.day} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-fg">{formatDayTitle(p.day)}</div>
                    <div className="text-xs tabular-nums text-muted">
                      {n0(p.kcal)} ккал
                      {p.burned_kcal !== null ? ` (расход ${n0(p.burned_kcal)})` : ''}
                      {` · Б ${n0(p.protein_g)} · Ж ${n0(p.fat_g)} · У ${n0(p.carbs_g)}`}
                      {p.water_ml !== null ? ` · вода ${n0(p.water_ml)} мл` : ''}
                      {p.note ? ` · ${p.note}` : ''}
                    </div>
                  </div>
                  <DeleteButton
                    what={`питание за ${formatDate(p.day)}`}
                    pending={remove.isPending}
                    error={remove.error}
                    onConfirm={() => remove.mutateAsync(p.day).then(() => toast.show({ message: 'Удалено' }))}
                  />
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
      <Sheet open={open} onClose={() => setOpen(false)} title="Питание за день">
        <NutritionForm entries={entries} onDone={() => setOpen(false)} />
      </Sheet>
    </div>
  )
}
